<template>
  <template v-if="showToolbar">
    <ZtTooltip v-if="showUpsell" content="OCR is available on paid plans as part of our AI features" placement="bottom">
      <a
        class="ocr-toolbar__upsell"
        href="/plans?utm_campaign=upgrade&utm_medium=tooltip&utm_source=viewer"
        aria-disabled="true"
        data-testid="viewer-topbar-ocr-upsell"
      >
        <Icon name="general.copyFileFolder" size="base" />
        <span>Copy text</span>
      </a>
    </ZtTooltip>
    <span v-else-if="showProcessing && isOcrProcessing" class="ocr-toolbar__processing" data-testid="viewer-topbar-ocr-processing"> Detecting text... </span>
    <ZtTooltip v-else-if="isOcrDone && !isOcrAvailable" content="No text was detected in this image" placement="bottom">
      <span class="ocr-toolbar__processing" data-testid="viewer-topbar-ocr-no-text">No text</span>
    </ZtTooltip>
    <template v-else-if="isOcrAvailable">
      <template v-if="!isSelectionMode">
        <ContextMenuWrapper placement="bottom-start" test-id="viewer-topbar-copy-text" @open="isOpen = true" @close="isOpen = false">
          <button type="button" class="ocr-toolbar__menu-trigger" aria-haspopup="menu" :aria-expanded="isOpen" data-testid="viewer-topbar-copy-text-trigger">
            <Icon name="general.copyFileFolder" size="base" />
            <span>Copy text</span>
          </button>
          <template #menu>
            <MenuLink icon="general.textFields" title="Select text" data-testid="viewer-topbar-text-select" @click="enterSelectionMode">Select text</MenuLink>
            <MenuLink icon="general.copyFileFolder" title="Copy all text" data-testid="viewer-topbar-copy-all-text" @click="copyAllText"
              >Copy all text</MenuLink
            >
          </template>
        </ContextMenuWrapper>
      </template>
      <template v-else>
        <button class="ocr-toolbar__chip" title="Cancel selection" data-testid="viewer-topbar-cancel-select" @click="exitSelectionMode">
          <span>{{ selectedCount }} selected</span>
          <Icon name="general.close" size="xs" />
        </button>
        <Button
          type="solid"
          variant="blue"
          icon="general.copyFileFolder"
          :disabled="!hasSelection"
          data-testid="viewer-topbar-copy-selected"
          @click="copySelectedText"
        >
          Copy
        </Button>
      </template>
    </template>

    <!-- Only render the separator when there are real OCR tools to its left;
         the "Detecting..." label alone shouldn't get a trailing divider. -->
    <span v-if="showUpsell || isOcrAvailable || (isOcrDone && !isOcrAvailable)" class="ocr-toolbar__separator" />
  </template>
  <span v-else class="ocr-toolbar__placeholder" aria-hidden="true" />
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { Icon, Button, ContextMenuWrapper, MenuLink } from '@/storybook';
import { ZtTooltip } from '@/storybook/tooltip';
import { useOcrActions } from '@/modules/viewer/composables/useOcrActions';

const props = defineProps<{
  /** Owner opt-in: show "Detecting text..." label during OCR processing. */
  showProcessing?: boolean;
  /** Owner without OCR entitlement: display an upgrade affordance instead of hiding the feature. */
  showUpsell?: boolean;
}>();

const {
  isOcrAvailable,
  isOcrProcessing,
  isOcrDone,
  isSelectionMode,
  selectedCount,
  hasSelection,
  enterSelectionMode,
  exitSelectionMode,
  copyAllText,
  copySelectedText,
} = useOcrActions();

const isOpen = ref(false);

// Gated so the trailing separator doesn't orphan when OCR is inactive.
const showToolbar = computed(() => props.showUpsell || isOcrAvailable.value || isOcrDone.value || (props.showProcessing && isOcrProcessing.value));
</script>

<style scoped>
.ocr-toolbar__processing {
  @apply text-xs text-zinc-500 dark:text-zinc-400;
  min-width: 105px;
  min-height: 40px;
  display: inline-flex;
  align-items: center;
}

.ocr-toolbar__placeholder {
  width: 105px;
  min-height: 40px;
  flex-shrink: 0;
}

.ocr-toolbar__upsell {
  @apply flex items-center gap-1.5 p-2 rounded-md text-sm text-zinc-400 dark:text-zinc-500 opacity-60 cursor-not-allowed;
  min-width: 40px;
  min-height: 40px;
}

.ocr-toolbar__menu-trigger {
  @apply flex items-center gap-1.5 p-2 rounded-md text-sm text-zinc-600 dark:text-zinc-400 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors;
  min-width: 40px;
  min-height: 40px;
}

.ocr-toolbar__chip {
  @apply flex items-center gap-1 sm:gap-1.5 px-2 sm:px-2.5 py-1 text-xs font-medium rounded-full cursor-pointer transition-colors whitespace-nowrap;
  @apply text-zinc-700 dark:text-zinc-300 bg-zinc-100 dark:bg-zinc-700 hover:bg-zinc-200 dark:hover:bg-zinc-600;
}

.ocr-toolbar__separator {
  @apply w-px h-5 bg-zinc-300 dark:bg-zinc-600 mx-1;
}
</style>
